// #3606 - "Update now" (account menu, CheckForUpdatesDialog) must end in a
// full reload into the new build, and must never leave the tablet frozen.
//
// Why not vite-plugin-pwa's updateServiceWorker(true): that function is bound
// to the one workbox instance that created it. It posts SKIP_WAITING through
// that instance's own registration (a silent no-op until the instance has
// finished registering) and reloads only from a "controlling" listener that
// instance attaches when IT saw the waiting worker. The dialog mounts a second
// instance after the check already found the worker, so either half can be
// missing: nothing is posted, nothing reloads, and the dialog (which disables
// Cancel and ignores Escape while updating) is a permanent lock. The scribe
// had to kill the PWA.
//
// This talks to the browser directly instead:
//   1. ask the registration's waiting worker to skipWaiting (the generated
//      sw.js handles { type: "SKIP_WAITING" } in registerType "prompt"),
//   2. reload when the browser reports the new worker took control,
//   3. reload anyway after `timeoutMs` if that never happens,
//   4. reload at once if there is nothing waiting to activate.
// Reload runs at most once. Returns a cancel() that disarms the backstop timer
// and the controllerchange listener and guarantees no reload follows (#3621:
// the product dialog never cancels, since an update in flight must still end in
// a reload; tests use it so one test's 5s backstop cannot fire into the next).
// A reload that lands before the swap completes
// still ends on a live app (the old shell, which the next check re-offers
// the update for), never a frozen dialog.

export const DEFAULT_TAKEOVER_TIMEOUT_MS = 5000;

export function applyWaitingUpdate({
  timeoutMs = DEFAULT_TAKEOVER_TIMEOUT_MS,
  reload = () => window.location.reload(),
} = {}) {
  const container = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
  let reloaded = false;
  const reloadOnce = () => {
    if (reloaded) return;
    reloaded = true;
    clearTimeout(timer);
    container?.removeEventListener?.("controllerchange", reloadOnce);
    reload();
  };

  // The backstop is armed before anything async so no failure below can skip it.
  const timer = setTimeout(reloadOnce, timeoutMs);
  const cancel = () => {
    reloaded = true;
    clearTimeout(timer);
    container?.removeEventListener?.("controllerchange", reloadOnce);
  };
  if (!container) {
    reloadOnce();
    return cancel;
  }
  container.addEventListener?.("controllerchange", reloadOnce);

  Promise.resolve()
    .then(() => container.getRegistration())
    .then((registration) => {
      const waiting = registration?.waiting;
      if (waiting) waiting.postMessage({ type: "SKIP_WAITING" });
      else reloadOnce();
    })
    .catch(reloadOnce);

  return cancel;
}
